import { DEFAULT_IFRAME_EMBED, IFRAME_ELEMENT_DEFAULT_HEIGHT, IFRAME_ELEMENT_DEFAULT_WIDTH } from './constants.js';

const IFRAME_ALLOW = 'autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture';

/**
 * Parses iframe value - accepts full iframe HTML or URL string.
 * @param {string} iframeValue
 * @returns {{ html: string|null, src: string }}
 */
function parseIframeValue(iframeValue) {
    if (!iframeValue || typeof iframeValue !== 'string') return { html: null, src: '' };
    const trimmed = iframeValue.trim();
    if (trimmed.toLowerCase().startsWith('<iframe')) {
        return { html: trimmed, src: '' };
    }
    return { html: null, src: trimmed };
}

function parsePixelDimension(value, fallback) {
    if (value == null || value === '') return fallback;
    const str = String(value).trim();
    if (str.endsWith('%')) return fallback;
    const num = parseInt(str, 10);
    return Number.isFinite(num) && num > 0 ? num : fallback;
}

/**
 * IframeElement - DOM content for CSS3D iframe embeds (VRIframeElement).
 */
class IframeElement {
    static DEFAULT_EMBED = DEFAULT_IFRAME_EMBED;

    static parseIframeValue = parseIframeValue;

    static resolveDimensions(iframeValue, dbConfig = {}) {
        const width = Number(dbConfig.width) > 0
            ? Number(dbConfig.width)
            : IFRAME_ELEMENT_DEFAULT_WIDTH;
        let height = Number(dbConfig.height) > 0
            ? Number(dbConfig.height)
            : IFRAME_ELEMENT_DEFAULT_HEIGHT;

        const { html } = parseIframeValue(iframeValue);
        if (html) {
            const match = html.match(/<iframe\b[^>]*>/i);
            if (match) {
                const tag = match[0];
                const widthAttr = tag.match(/\bwidth=["']?([^"'\s>]+)/i)?.[1];
                const heightAttr = tag.match(/\bheight=["']?([^"'\s>]+)/i)?.[1];
                const parsedHeight = parsePixelDimension(heightAttr, height);
                const parsedWidth = parsePixelDimension(widthAttr, width);
                return {
                    width: parsedWidth,
                    height: parsedHeight,
                };
            }
        }

        return { width, height };
    }

    constructor(options = {}) {
        this.dbConfig = options.dbConfig || {};
        this.iframeValue = options.iframeValue ?? this.dbConfig.iframe_value ?? '';
        const dims = IframeElement.resolveDimensions(this.iframeValue, this.dbConfig);
        this.width = options.width ?? dims.width;
        this.height = options.height ?? dims.height;
        this.interactive = options.interactive !== false;
    }

    render() {
        const div = document.createElement('div');
        div.setAttribute('class', 'vrai-component-slot vr-iframe-element-app');
        div.setAttribute('data-vrai-component', 'iframe');
        div.innerHTML = `
            <div class="vrai-component-content vr-iframe-element-content" style="position:absolute;inset:0;overflow:hidden;">
                <div class="vr-iframe-element-slot"></div>
            </div>
        `;
        this._root = div;
        this._slot = div.querySelector('.vr-iframe-element-slot');
        this.updateIframe(this.iframeValue);
        return div;
    }

    getContentElement() {
        return this._root?.querySelector('.vrai-component-content');
    }

    updateIframe(iframeValue) {
        this.iframeValue = iframeValue || '';
        if (!this._slot) return;

        const { html, src } = parseIframeValue(this.iframeValue);
        this._slot.innerHTML = '';
        this._slot.classList.toggle('vr-iframe-element-empty', !html && !src);

        if (html) {
            this._slot.innerHTML = html;
            const iframeEl = this._slot.querySelector('iframe');
            if (iframeEl) {
                iframeEl.setAttribute('allow', IFRAME_ALLOW);
                iframeEl.style.width = '100%';
                iframeEl.style.height = '100%';
                iframeEl.style.border = 'none';
                iframeEl.style.display = 'block';
                iframeEl.style.pointerEvents = this.interactive ? 'auto' : 'none';
            }
            return;
        }

        if (src) {
            const iframe = document.createElement('iframe');
            iframe.setAttribute('allowfullscreen', 'true');
            iframe.setAttribute('allow', IFRAME_ALLOW);
            iframe.setAttribute('title', 'iframe');
            iframe.src = src;
            iframe.style.width = '100%';
            iframe.style.height = '100%';
            iframe.style.border = 'none';
            iframe.style.pointerEvents = this.interactive ? 'auto' : 'none';
            this._slot.appendChild(iframe);
            return;
        }

        const label = (typeof $trans !== 'undefined' && $trans('iframe-text')) || 'iFrame';
        this._slot.innerHTML = `<div class="vr-iframe-element-placeholder"><span class="material-icons-outlined">web</span><span>${label}</span></div>`;
    }

    setInteractive(enabled) {
        this.interactive = enabled !== false;
        this._slot?.querySelectorAll('iframe').forEach((iframe) => {
            iframe.style.pointerEvents = this.interactive ? 'auto' : 'none';
        });
    }
}

export { IframeElement, parseIframeValue };
